import { useEffect } from 'react';
import {
  Box,
  COLORS,
  Divider,
  Flex,
  Heading,
  Icon,
  Text,
} from '@podiumhq/design-system';

import PodiumLogoIcon from 'components/PodiumLogo/PodiumLogoIcon';
import { YEARLY_TERM } from 'components/PricingPackaging/constants';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';
import useTrackAmplitudeEvent from 'hooks/useTrackAmplitudeEvent';

import BillingDetails from './components/BillingDetails';
import ContactDetails from './components/ContactDetails';
import { CheckoutDescription, CheckoutTitle } from './components/layout';
import PaymentDetails from './components/PaymentDetails';
import SelectPlanTerm from './components/SelectPlanTerm';
import { useGetBillingAddress } from './hooks';
import { SelectPlanCheckoutButton } from './styles';

const PaymentConfirmationView = () => {
  const addressDetails = useGetBillingAddress();
  const {
    isMobile,
    redirectAfterPurchase,
    selectedPlan,
    selectedTerm,
    userDetails,
  } = useCheckoutContext() || {};
  const trackEvent = useTrackAmplitudeEvent(
    'selectPlanCheckout.paymentConfirmation.visited'
  );

  const {
    annualPrice = '',
    displayName: planName = '',
    monthlyPrice = '',
  } = selectedPlan || {};
  const { firstName = '', email = '' } = userDetails || {};

  const contentWidth = isMobile ? '100%' : '400px';
  const price = selectedTerm === YEARLY_TERM ? annualPrice : monthlyPrice;
  const term = 'month';

  const redirectToPodium = () => {
    trackEvent('selectPlanCheckout.paymentConfirmation.podiumRedirectClicked');

    redirectAfterPurchase && redirectAfterPurchase();
  };

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Box width={contentWidth}>
        <CheckoutTitle alignItems="center" display="flex">
          <Icon
            boxSize={10}
            color="green.base"
            mr="16px"
            type="CheckCircleSolid"
          />
          Thanks, {firstName}!
        </CheckoutTitle>

        <CheckoutDescription {...descriptionStyles} mb="16px">
          Congrats on your Podium plan. Thanks for choosing Podium to grow your
          business. Let’s get to work.
        </CheckoutDescription>

        <CheckoutDescription display="inline">
          You’ll receive a confirmation email shortly at{' '}
        </CheckoutDescription>
        <CheckoutDescription display="inline" fontWeight="semibold">
          {email}
        </CheckoutDescription>

        <Box
          border={`1px solid ${COLORS.grey.lightest}`}
          borderRadius="12px"
          margin="24px 0 20px 0"
          padding="24px"
          width={contentWidth}
        >
          <Flex>
            <PodiumLogoIcon height="48px" mr="16px" width="48px" />

            <Box>
              <Heading color="black.base" mb="2px" {...boldTextStyles}>
                {planName}
              </Heading>

              <Text fontSize="md" fontWeight="normal" lineHeight="150%">
                ${price} USD / {term} + taxes
              </Text>
            </Box>
          </Flex>

          <Divider margin="24px 0" />

          <ContactDetails />

          <SelectPlanTerm />

          <PaymentDetails mb="24px" />

          <BillingDetails addressDetails={addressDetails} />
        </Box>

        <SelectPlanCheckoutButton
          fillColor="blue.base"
          id="confirmation-receipt-button"
          margin="12px 0 20px 0"
          onClick={redirectToPodium}
          width="100%"
        >
          Got it
        </SelectPlanCheckoutButton>
      </Box>
    </>
  );
};

const boldTextStyles = {
  fontSize: 'lg',
  fontWeight: 'medium',
  lineHeight: '150%',
};

const descriptionStyles = { mt: '16px' };

export default PaymentConfirmationView;
